import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import type { VendorOptimizationRun } from "../../api/types";
import { S } from "../../styles";
import { LoadingState } from "../../components/QueryStates";

export function VendorOptimizationPanel() {
  const queryClient = useQueryClient();
  const [msg, setMsg] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["vendor-optimization-runs"],
    queryFn: () => api.listVendorOptimizationRuns(),
  });
  const runs = data?.items ?? [];
  const readyRuns = runs.filter((r) => r.status === "ready");

  const invalidate = () =>
    void queryClient.invalidateQueries({
      queryKey: ["vendor-optimization-runs"],
    });

  const triggerMutation = useMutation({
    mutationFn: () => api.triggerVendorOptimization(90),
    onSuccess: (r) => {
      setMsg(
        `⚙ Analysis started: ${r.workflow_id}. Results will appear shortly.`,
      );
      setTimeout(invalidate, 3000);
    },
    onError: () => setMsg("❌ Failed to trigger optimization."),
  });

  const applyMutation = useMutation({
    mutationFn: (id: string) => api.applyVendorOptimizationRun(id),
    onSuccess: () => {
      setMsg("✅ Recommendations marked as applied.");
      invalidate();
    },
    onError: () => setMsg("❌ Failed to apply run."),
  });

  const dismissMutation = useMutation({
    mutationFn: (id: string) => api.dismissVendorOptimizationRun(id),
    onSuccess: () => invalidate(),
    onError: () => setMsg("❌ Failed to dismiss run."),
  });

  const handleExport = (run: VendorOptimizationRun) => {
    const header =
      "Category,From Supplier,To Supplier,Shift %,Current Spend USD,Expected Savings USD,Rationale";
    const rows = run.recommendations.map((r) =>
      [
        r.category,
        r.from_supplier_name,
        r.to_supplier_name,
        r.recommended_shift_pct.toFixed(0),
        r.current_spend_usd.toFixed(2),
        r.expected_savings_usd.toFixed(2),
        `"${r.rationale.replace(/"/g, '""')}"`,
      ].join(","),
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vendor-optimization-${run.id.slice(0, 8)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <h2 style={S.h2}>📈 Vendor Optimization</h2>
        <button style={S.btnSm} onClick={() => void refetch()}>
          ↺ Refresh
        </button>
        <button
          style={S.btn}
          onClick={() => triggerMutation.mutate()}
          disabled={triggerMutation.isPending}
        >
          {triggerMutation.isPending ? "Starting…" : "⚙ Run Analysis"}
        </button>
      </div>
      {msg && (
        <div style={{ ...S.card, background: "#e3f2fd", marginBottom: 12 }}>
          {msg}
        </div>
      )}
      {isLoading ? (
        <LoadingState label="Loading optimization runs…" />
      ) : readyRuns.length === 0 ? (
        <div style={S.card}>
          <p style={S.muted}>No optimization analyses available.</p>
          <p style={S.muted}>
            Click "Run Analysis" to start a spend vs. scorecard analysis for
            your suppliers.
          </p>
        </div>
      ) : (
        readyRuns.map((run) => (
          <div key={run.id} style={{ ...S.card, marginBottom: 12 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <div>
                <div style={{ fontWeight: 700 }}>
                  Analysis — {run.analysis_period_days}-day period
                </div>
                <div style={S.muted}>
                  Generated:{" "}
                  {run.generated_at
                    ? new Date(run.generated_at).toLocaleString()
                    : "—"}{" "}
                  · {run.recommendations.length} recommendations
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  style={S.btnGhost}
                  onClick={() =>
                    setExpanded(expanded === run.id ? null : run.id)
                  }
                >
                  {expanded === run.id ? "Hide" : "Show"}
                </button>
                <button style={S.btnGhost} onClick={() => handleExport(run)}>
                  Export CSV
                </button>
                <button
                  style={S.btn}
                  onClick={() => applyMutation.mutate(run.id)}
                  disabled={applyMutation.isPending}
                >
                  Apply
                </button>
                <button
                  style={S.btnGhost}
                  onClick={() => dismissMutation.mutate(run.id)}
                  disabled={dismissMutation.isPending}
                >
                  Dismiss
                </button>
              </div>
            </div>
            {run.reasoning && (
              <p style={{ ...S.muted, marginTop: 8, fontStyle: "italic" }}>
                {run.reasoning}
              </p>
            )}
            {expanded === run.id &&
              run.recommendations.map((rec, i) => (
                <div
                  key={i}
                  style={{
                    marginTop: 10,
                    padding: "10px 14px",
                    background: "#f0f7ff",
                    borderRadius: 6,
                    borderLeft: "3px solid #1976d2",
                  }}
                >
                  <div style={{ fontWeight: 600 }}>
                    Shift {rec.recommended_shift_pct.toFixed(0)}% from{" "}
                    {rec.from_supplier_name} → {rec.to_supplier_name}
                  </div>
                  <div style={S.muted}>
                    Category: {rec.category} · Current spend: $
                    {rec.current_spend_usd.toLocaleString()} · Expected savings:
                    ${rec.expected_savings_usd.toLocaleString()}
                  </div>
                  <p style={{ ...S.muted, fontStyle: "italic", marginTop: 4 }}>
                    {rec.rationale}
                  </p>
                </div>
              ))}
          </div>
        ))
      )}
    </div>
  );
}
